import {
  Box,
  Header,
  Link,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { listDripCampaigns } from '../../../common/api/dripCampaigns';
import { DashboardViewLayout } from '../../../components/DashboardViewLayout';

const DAYS = 30;

export default function DripCampaignsIndexPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['drip-campaigns', DAYS],
    queryFn: () => listDripCampaigns(DAYS),
    refetchInterval: 30_000,
  });

  const items = data ?? [];

  return (
    <DashboardViewLayout
      breadcrumbs={[
        { text: 'Drip', href: '/dashboard/drip/dashboard' },
        { text: 'Campaigns', href: '/dashboard/drip/campaigns' },
      ]}
      contentType="table"
    >
      <Table
        variant="full-page"
        stickyHeader
        loading={isLoading}
        loadingText="Loading campaigns…"
        items={items}
        empty={
          <Box color="inherit" margin={{ vertical: 'xs' }} textAlign="center">
            {error ? (
              <SpaceBetween size="xxs">
                <div>
                  <b>Failed to load campaigns</b>
                  <Box color="inherit" variant="p">
                    {String(error)}
                  </Box>
                </div>
              </SpaceBetween>
            ) : (
              <SpaceBetween size="xxs">
                <div>
                  <b>No campaigns yet</b>
                  <Box color="inherit" variant="p">
                    Submit a zip blast to bootstrap one.
                  </Box>
                </div>
              </SpaceBetween>
            )}
          </Box>
        }
        header={
          <Header
            variant="awsui-h1-sticky"
            counter={`(${items.length})`}
            description={`Top-line stats for the last ${DAYS} days. Click any campaign for daily time-series, top-clicked URLs, and per-restaurant funnel.`}
          >
            Drip Campaigns
          </Header>
        }
        columnDefinitions={[
          {
            id: 'name',
            header: 'Name',
            cell: (i) => (
              <Link
                href={`/dashboard/drip/campaigns/${i.uuid}`}
                onFollow={(e) => {
                  e.preventDefault();
                  navigate({
                    to: '/dashboard/drip/campaigns/$uuid',
                    params: { uuid: i.uuid },
                  });
                }}
              >
                {i.name}
              </Link>
            ),
            minWidth: 260,
          },
          {
            id: 'template',
            header: 'Template',
            cell: (i) => i.current_template_version ?? '—',
            width: 220,
          },
          {
            id: 'sent',
            header: `Sent (${DAYS}d)`,
            cell: (i) => i.current.sent.toLocaleString(),
            width: 120,
          },
          {
            id: 'opened',
            header: 'Opened',
            cell: (i) => i.current.opened.toLocaleString(),
            width: 100,
          },
          {
            id: 'clicked',
            header: 'Clicked',
            cell: (i) => i.current.clicked.toLocaleString(),
            width: 100,
          },
          {
            id: 'bounced',
            header: 'Bounced',
            cell: (i) => i.current.bounced.toLocaleString(),
            width: 100,
          },
          {
            id: 'unsub',
            header: 'Unsubs',
            cell: (i) => i.current.unsubscribed.toLocaleString(),
            width: 100,
          },
          {
            id: 'future',
            header: `Scheduled (next ${DAYS}d)`,
            cell: (i) => i.future.scheduled.toLocaleString(),
            width: 200,
          },
        ]}
      />
    </DashboardViewLayout>
  );
}
